import React, { createContext, useReducer, useContext, useMemo, useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

// Initial state
const initialState = {
  projects: [],
  currentProject: null,
  schemas: [],
  statistics: {
    overview: {
      total_projects: 0,
      active_projects: 0,
      total_processed_files: 0,
      total_error_files: 0,
      success_rate: 0.0
    },
    project_status_breakdown: {},
    recent_projects: [],
    timestamp: null
  },
  monitoringData: {
    totalProcessed: 0,
    errors: 0,
    avgProcessingTime: 0,
    lastProcessed: null,
    logs: [],
    activities: [],
  },
  loading: false,
  error: null,
};

// Action types
const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_PROJECTS: 'SET_PROJECTS',
  SET_CURRENT_PROJECT: 'SET_CURRENT_PROJECT',
  SET_SCHEMAS: 'SET_SCHEMAS',
  SET_STATISTICS: 'SET_STATISTICS',
  SET_MONITORING_DATA: 'SET_MONITORING_DATA',
  ADD_PROJECT: 'ADD_PROJECT',
  UPDATE_PROJECT: 'UPDATE_PROJECT',
  DELETE_PROJECT: 'DELETE_PROJECT',
  ADD_SCHEMA: 'ADD_SCHEMA',
  UPDATE_SCHEMA: 'UPDATE_SCHEMA',
  DELETE_SCHEMA: 'DELETE_SCHEMA',
  CLEAR_ERROR: 'CLEAR_ERROR',
  RESET_STATE: 'RESET_STATE',
};

// Reducer
const documentParserReducer = (state, action) => {
  
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case ACTION_TYPES.SET_PROJECTS:
      return {
        ...state,
        projects: action.payload,
        loading: false,
        error: null,
      };

    case ACTION_TYPES.SET_CURRENT_PROJECT:
      return {
        ...state,
        currentProject: action.payload,
        loading: false,
        error: null,
      };

    case ACTION_TYPES.SET_SCHEMAS:
      return {
        ...state,
        schemas: action.payload,
        loading: false,
        error: null,
      };

    case ACTION_TYPES.SET_STATISTICS:
      return {
        ...state,
        statistics: action.payload,
        loading: false,
        error: null,
      };

    case ACTION_TYPES.SET_MONITORING_DATA:
      return {
        ...state,
        monitoringData: action.payload,
        loading: false,
        error: null,
      };

    case ACTION_TYPES.ADD_PROJECT:
      return {
        ...state,
        projects: [...state.projects, action.payload],
        loading: false,
        error: null,
      };

    case ACTION_TYPES.UPDATE_PROJECT:
      // 防禦性檢查：確保 payload 包含必要的欄位
      if (!action.payload || (!action.payload.document_project_id && !action.payload.id)) {
        console.warn('UPDATE_PROJECT: Invalid payload, skipping update', action.payload);
        return state;
      }
      
      // 確保 project_name 存在，防止 sidebar 顯示錯誤
      const validatedPayload = {
        ...action.payload,
        project_name: action.payload.project_name || action.payload.name || 'Unknown Project'
      };

      const updatedProjects = state.projects.map((project) => {
        // 🔥 修復：只使用 document_project_id 進行比對，避免 id 欄位的問題
        const isTargetProject = project.document_project_id === validatedPayload.document_project_id;
        
        if (isTargetProject) {
          // 合併更新而非完全替換，保留原有的重要欄位
          return {
            ...project,
            ...validatedPayload,
            // 確保關鍵欄位不會丟失
            document_project_id: validatedPayload.document_project_id || project.document_project_id,
            project_name: validatedPayload.project_name || project.project_name
          };
        }
        return project;
      });

      return {
        ...state,
        projects: updatedProjects,
        currentProject: (state.currentProject?.document_project_id === validatedPayload.document_project_id || 
                         state.currentProject?.id === validatedPayload.id) ? validatedPayload : state.currentProject,
        loading: false,
        error: null,
      };

    case ACTION_TYPES.DELETE_PROJECT:
      return {
        ...state,
        projects: state.projects.filter(project => 
          project.document_project_id !== action.payload && project.id !== action.payload
        ),
        currentProject: (state.currentProject?.document_project_id === action.payload || 
                         state.currentProject?.id === action.payload) ? null : state.currentProject,
        loading: false,
        error: null,
      };

    case ACTION_TYPES.ADD_SCHEMA:
      return {
        ...state,
        schemas: [...state.schemas, action.payload],
        loading: false,
        error: null,
      };

    case ACTION_TYPES.UPDATE_SCHEMA:
      return {
        ...state,
        schemas: state.schemas.map(schema =>
          schema.id === action.payload.id ? action.payload : schema
        ),
        loading: false,
        error: null,
      };

    case ACTION_TYPES.DELETE_SCHEMA:
      return {
        ...state,
        schemas: state.schemas.filter(schema => schema.id !== action.payload),
        loading: false,
        error: null,
      };

    case ACTION_TYPES.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ACTION_TYPES.RESET_STATE:
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

// Context
export const DocumentParserContext = createContext();

// Provider component
export const DocumentParserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(documentParserReducer, initialState);
  const { t } = useTranslation();

  // Actions with useCallback to prevent unnecessary re-renders
  const setLoading = useCallback((loading) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error });
    if (error) {
      message.error(error);
    }
  }, []);

  const setProjects = useCallback((projects) => {
    dispatch({ type: ACTION_TYPES.SET_PROJECTS, payload: projects });
  }, []);

  const setCurrentProject = useCallback((project) => {
    dispatch({ type: ACTION_TYPES.SET_CURRENT_PROJECT, payload: project });
  }, []);

  const setSchemas = useCallback((schemas) => {
    dispatch({ type: ACTION_TYPES.SET_SCHEMAS, payload: schemas });
  }, []);

  const setStatistics = useCallback((statistics) => {
    dispatch({ type: ACTION_TYPES.SET_STATISTICS, payload: statistics });
  }, []);

  const setMonitoringData = useCallback((monitoringData) => {
    dispatch({ type: ACTION_TYPES.SET_MONITORING_DATA, payload: monitoringData });
  }, []);

  const addProject = useCallback((project) => {
    dispatch({ type: ACTION_TYPES.ADD_PROJECT, payload: project });
    message.success(t('documentParser.projectCreated'));
  }, [t]);

  const updateProject = useCallback((project) => {
    dispatch({ type: ACTION_TYPES.UPDATE_PROJECT, payload: project });
    message.success(t('documentParser.projectUpdated'));
  }, [t]);

  const deleteProject = useCallback((projectId) => {
    dispatch({ type: ACTION_TYPES.DELETE_PROJECT, payload: projectId });
    message.success(t('documentParser.projectDeleted'));
  }, [t]);

  const addSchema = useCallback((schema) => {
    dispatch({ type: ACTION_TYPES.ADD_SCHEMA, payload: schema });
    message.success(t('documentParser.schemaCreated'));
  }, [t]);

  const updateSchema = useCallback((schema) => {
    dispatch({ type: ACTION_TYPES.UPDATE_SCHEMA, payload: schema });
    message.success(t('documentParser.schemaUpdated'));
  }, [t]);

  const deleteSchema = useCallback((schemaId) => {
    dispatch({ type: ACTION_TYPES.DELETE_SCHEMA, payload: schemaId });
    message.success(t('documentParser.schemaDeleted'));
  }, [t]);

  const clearError = useCallback(() => {
    dispatch({ type: ACTION_TYPES.CLEAR_ERROR });
  }, []);

  const resetState = useCallback(() => {
    dispatch({ type: ACTION_TYPES.RESET_STATE });
  }, []);

  // Context value with useMemo to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    state,
    actions: {
      setLoading,
      setError,
      setProjects,
      setCurrentProject,
      setSchemas,
      setStatistics,
      setMonitoringData,
      addProject,
      updateProject,
      deleteProject,
      addSchema,
      updateSchema,
      deleteSchema,
      clearError,
      resetState,
    },
  }), [
    state,
    setLoading,
    setError,
    setProjects,
    setCurrentProject,
    setSchemas,
    setStatistics,
    setMonitoringData,
    addProject,
    updateProject,
    deleteProject,
    addSchema,
    updateSchema,
    deleteSchema,
    clearError,
    resetState,
  ]);

  return (
    <DocumentParserContext.Provider value={contextValue}>
      {children}
    </DocumentParserContext.Provider>
  );
};

// Custom hook to use the context
export const useDocumentParserContext = () => {
  const context = useContext(DocumentParserContext);
  if (!context) {
    throw new Error('useDocumentParserContext must be used within a DocumentParserProvider');
  }
  return context;
};

export default DocumentParserContext;